import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Star, Trash2, Copy } from "lucide-react";
import { brokerApi } from "@/hooks/useBrokerApi";

const STATUS_OPTIONS = ["invited", "connected", "declined"];

const NetworkCodeBox = ({ code }) => {
  const copyCode = () => {
    navigator.clipboard.writeText(code);
    toast.success("Network code copied — share it with drivers so they can join directly.");
  };
  return (
    <div data-testid="broker-network-code-box" className="bg-rr-navy/5 border border-rr-navy/15 rounded-xl p-4 mb-6 flex flex-wrap items-center justify-between gap-3">
      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-rr-muted">Your Network Join Code</p>
        <p className="font-heading text-xl font-extrabold text-rr-navy" data-testid="broker-network-code-value">{code}</p>
        <p className="text-xs text-rr-muted mt-1">Share this with drivers so they can join your network directly from their Driver Portal.</p>
      </div>
      <button onClick={copyCode} data-testid="broker-network-code-copy-button" className="flex items-center gap-1.5 text-xs font-bold bg-rr-navy text-white px-3 py-2 rounded-full hover:bg-rr-orange transition-colors">
        <Copy size={12} /> Copy Code
      </button>
    </div>
  );
};

const MyDriverRow = ({ connection: c, groups, onPatch, onRemove, onToggleGroup }) => (
  <div data-testid={`my-driver-row-${c.id}`} className="bg-white border border-rr-line rounded-xl p-5">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <div className="flex items-center gap-2">
          <p className="font-heading font-bold text-rr-navy">{c.invited_name}</p>
          <button onClick={() => onPatch(c.id, { preferred: !c.preferred })} data-testid={`my-driver-preferred-toggle-${c.id}`} title="Preferred driver">
            <Star size={15} className={c.preferred ? "fill-rr-orange text-rr-orange" : "text-rr-line"} />
          </button>
        </div>
        <p className="text-xs text-rr-muted mt-0.5">{c.invited_email}{c.invited_phone && ` · ${c.invited_phone}`}</p>
      </div>
      <div className="flex items-center gap-2">
        <select
          value={c.status}
          onChange={(e) => onPatch(c.id, { status: e.target.value }, "Status updated.")}
          data-testid={`my-driver-status-select-${c.id}`}
          className="border border-rr-line rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:border-rr-orange capitalize"
        >
          {STATUS_OPTIONS.map((s) => (<option key={s} value={s}>{s}</option>))}
        </select>
        <button onClick={() => onRemove(c.id)} data-testid={`my-driver-remove-button-${c.id}`} className="text-rr-red hover:opacity-70">
          <Trash2 size={15} />
        </button>
      </div>
    </div>
    {groups.length > 0 && (
      <div className="flex flex-wrap gap-1.5 mt-3">
        {groups.map((g) => (
          <button
            key={g.id}
            onClick={() => onToggleGroup(c, g.id)}
            data-testid={`my-driver-group-chip-${c.id}-${g.id}`}
            className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${
              c.group_ids.includes(g.id) ? "bg-rr-navy text-white border-rr-navy" : "border-rr-line text-rr-muted"
            }`}
          >
            {g.name}
          </button>
        ))}
      </div>
    )}
    <textarea
      defaultValue={c.internal_note || ""}
      onBlur={(e) => e.target.value !== (c.internal_note || "") && onPatch(c.id, { internal_note: e.target.value }, "Note saved.")}
      placeholder="Internal note (visible only to you)"
      data-testid={`my-driver-note-input-${c.id}`}
      rows={2}
      className="w-full mt-3 border border-rr-line rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-rr-orange resize-none"
    />
  </div>
);

export const BrokerMyDriversTab = () => {
  const [network, setNetwork] = useState(null);
  const [groups, setGroups] = useState([]);
  const [networkCode, setNetworkCode] = useState(null);

  const load = () => brokerApi.get("/network").then(({ data }) => setNetwork(data)).catch(() => setNetwork([]));
  useEffect(() => {
    load();
    brokerApi.get("/groups").then(({ data }) => setGroups(data)).catch(() => setGroups([]));
    brokerApi.get("/network-code").then(({ data }) => setNetworkCode(data.code)).catch(() => {});
  }, []);

  const patch = async (id, body, successMsg) => {
    try {
      await brokerApi.patch(`/network/${id}`, body);
      if (successMsg) toast.success(successMsg);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update this driver.");
    }
  };

  const remove = async (id) => {
    try {
      await brokerApi.del(`/network/${id}`);
      toast.success("Removed from your network.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't remove this driver.");
    }
  };

  const toggleGroup = (conn, groupId) => {
    const next = conn.group_ids.includes(groupId) ? conn.group_ids.filter((g) => g !== groupId) : [...conn.group_ids, groupId];
    patch(conn.id, { group_ids: next });
  };

  if (!network) return <div className="flex justify-center py-16"><Loader2 size={22} className="animate-spin text-rr-orange" /></div>;

  return (
    <div data-testid="broker-my-drivers-tab">
      <h2 className="font-heading text-xl font-bold text-rr-navy mb-5">My Drivers</h2>
      {networkCode && <NetworkCodeBox code={networkCode} />}
      {network.length === 0 && <p data-testid="my-drivers-empty-state" className="text-rr-muted text-sm">No drivers yet — add some from Find Drivers.</p>}
      <div className="space-y-3">
        {network.map((c) => (
          <MyDriverRow key={c.id} connection={c} groups={groups} onPatch={patch} onRemove={remove} onToggleGroup={toggleGroup} />
        ))}
      </div>
    </div>
  );
};
